import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Send, RefreshCw, Quote, CheckCircle2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const DailyQuoteTab = () => {
  const [quote, setQuote] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);
  const [error, setError] = useState('');

  const loadToday = async () => {
    setError('');
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/quote/daily?_t=${Date.now()}`, { cache: 'no-store' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setQuote(await r.json());
    } catch (e) {
      setError(`Failed to load: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/quote/history?limit=20`);
      const d = await r.json();
      setHistory(d.quotes || []);
    } catch { /* ignore */ }
  };

  const regenerate = async () => {
    setRegenerating(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/quote/regenerate`, { method: 'POST' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setQuote(await r.json());
    } catch (e) {
      setError(`Regenerate failed: ${e.message}`);
    } finally {
      setRegenerating(false);
    }
  };

  const broadcast = async (regen = false) => {
    if (!window.confirm(
      `Send today's quote to your public Telegram channel (@uppercircuitstocksbychiragrathod) right now?\n\n` +
      `All channel subscribers will receive a notification.`
    )) return;
    setBroadcasting(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/admin/quote/broadcast${regen ? '?regenerate=true' : ''}`, { method: 'POST' });
      const d = await r.json();
      if (d.broadcast?.ok) {
        alert('Broadcast successful — check your Telegram channel!');
      } else {
        alert(`Broadcast failed: ${d.broadcast?.reason || d.broadcast?.body || 'Unknown error'}`);
      }
      loadToday();
      loadHistory();
    } catch (e) {
      setError(`Broadcast failed: ${e.message}`);
    } finally {
      setBroadcasting(false);
    }
  };

  useEffect(() => {
    loadToday();
    loadHistory();
  }, []);

  return (
    <div className="space-y-6" data-testid="daily-quote-tab">
      <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Quote className="w-5 h-5 text-amber-600" />
                Daily Trader Quote
              </CardTitle>
              <CardDescription>
                Auto-broadcast to <strong>@uppercircuitstocksbychiragrathod</strong> daily at <strong>8:00 AM IST</strong>
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={regenerate} disabled={regenerating || broadcasting} size="sm" variant="outline" data-testid="quote-regenerate-btn">
                <RefreshCw className={`w-4 h-4 mr-1 ${regenerating ? 'animate-spin' : ''}`} />
                {regenerating ? 'Regenerating…' : 'Pick a different quote'}
              </Button>
              <Button onClick={() => broadcast(false)} disabled={broadcasting || !quote} size="sm" className="bg-green-600 hover:bg-green-700 text-white" data-testid="quote-broadcast-btn">
                <Send className={`w-4 h-4 mr-1 ${broadcasting ? 'animate-pulse' : ''}`} />
                {broadcasting ? 'Sending…' : 'Send to Telegram Now'}
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-3 text-sm">{error}</div>}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Today's Quote · {quote?.date || '—'}</CardTitle>
          <CardDescription className="text-xs">Preview of what subscribers will see in Telegram</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-gray-500">Loading…</div>
          ) : !quote ? (
            <p className="text-sm text-gray-500">No quote available.</p>
          ) : (
            <div className="bg-gradient-to-br from-zinc-900 to-black rounded-lg p-6 text-white border border-amber-500/30" data-testid="quote-preview">
              {quote.theme && <div className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-3">📜 Daily Trader Wisdom · {quote.theme}</div>}
              <blockquote className="text-white text-lg md:text-xl italic leading-relaxed mb-4">"{quote.quote}"</blockquote>
              <div className="text-amber-400 font-semibold">— {quote.author}</div>
              {quote.source_book && (
                <div className="text-gray-400 text-sm mt-1">
                  {quote.source_book}{quote.year ? ` · ${quote.year}` : ''}
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-zinc-800 text-xs text-zinc-500 italic">
                📈 uppercircuitstocks.com
              </div>
            </div>
          )}

          {quote?.telegram_broadcasted_at && (
            <div className="mt-3 flex items-center gap-2 text-xs">
              {quote.telegram_broadcast?.ok ? (
                <><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-green-700">Broadcast at {new Date(quote.telegram_broadcasted_at).toLocaleString('en-IN')}</span></>
              ) : (
                <><XCircle className="w-4 h-4 text-red-600" /><span className="text-red-700">Broadcast failed: {quote.telegram_broadcast?.reason || quote.telegram_broadcast?.body || 'unknown'}</span></>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Recent Quotes · Last 20 days</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-sm text-gray-500">No history yet.</p>
          ) : (
            <div className="space-y-2">
              {history.map((h, i) => (
                <div key={i} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-sm">{h.date} · <span className="text-gray-600">{h.author}</span></span>
                    {h.telegram_broadcasted_at && h.telegram_broadcast?.ok ? (
                      <Badge className="bg-green-100 text-green-800 text-[10px]">Sent</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">Not sent</Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-700 italic line-clamp-2">"{h.quote}"</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DailyQuoteTab;
